import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Table,
  Button,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Input,
  InputGroup,
  InputGroupAddon,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';

import Widget from '../../../components/Widget';
import DriverPayTab from './DriverPayTab';
import { fetchVehicles, deleteVehicle, updateVehicleState } from '../../../actions/vehicles';

class VehicleList extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      searchTerm: '',
      statusFilter: 'all',
      dropdownOpen: {},
      activeTab: 'vehicles',
      waitingList: [],
      waitingListLoading: false,
    };
  }

  componentDidMount() {
    this.props.dispatch(fetchVehicles());
    this.fetchWaitingList();
    // Auto-switch to waiting tab if redirected from onboarding
    const params = new URLSearchParams(this.props.location && this.props.location.search);
    if (params.get('tab') === 'waiting') {
      this.setState({ activeTab: 'waiting' });
    }
  }

  fetchWaitingList = async () => {
    this.setState({ waitingListLoading: true });
    try {
      const config = require('../../../config').default;
      const base = config.opsApiBase.replace('/api/v1/ops', '');
      const res = await fetch(`${base}/api/v1/ops/onboarding/waiting-list`);
      const data = await res.json();
      this.setState({ waitingList: data.success ? data.data : [] });
    } catch (e) {
      console.error('Failed to fetch waiting list:', e);
    } finally {
      this.setState({ waitingListLoading: false });
    }
  }

  toggleDropdown = (vehicleId) => {
    this.setState(prevState => ({
      dropdownOpen: {
        ...prevState.dropdownOpen,
        [vehicleId]: !prevState.dropdownOpen[vehicleId]
      }
    }));
  }

  handleSearch = (e) => {
    this.setState({ searchTerm: e.target.value });
  }

  handleStatusFilter = (status) => {
    this.setState({ statusFilter: status });
  }

  handleDeleteVehicle = async (vehicleId) => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await this.props.dispatch(deleteVehicle(vehicleId));
        // Refresh the list
        this.props.dispatch(fetchVehicles());
      } catch (error) {
        console.error('Failed to delete vehicle:', error);
      }
    }
  }

  handleUpdateVehicleState = async (vehicleId, newState) => {
    try {
      await this.props.dispatch(updateVehicleState(vehicleId, newState));
      // Refresh the list
      this.props.dispatch(fetchVehicles());
    } catch (error) {
      console.error('Failed to update vehicle state:', error);
    }
  }

  getStatusBadge = (status) => {
    const statusMap = {
      active: 'success',
      
      inactive: 'secondary',
     
    };
    return statusMap[status] || 'secondary';
  }

  filterVehicles = (vehicles) => {
    return vehicles.filter(vehicle => {
      const matchesSearch = (vehicle.registration_no || '').toLowerCase().includes(this.state.searchTerm.toLowerCase()) ||
                           (vehicle.make || '').toLowerCase().includes(this.state.searchTerm.toLowerCase()) ||
                           (vehicle.model || '').toLowerCase().includes(this.state.searchTerm.toLowerCase());
      
      const matchesStatus = this.state.statusFilter === 'all' || 
                           vehicle.status === this.state.statusFilter;
      return matchesSearch && matchesStatus;
    });
  }

  render() {
    const { vehicles = [], isFetching, error } = this.props;
    const { activeTab } = this.state;
    const filteredVehicles = this.filterVehicles(vehicles);

    if (error) {
      return (
        <div>
          <Breadcrumb>
            <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
            <BreadcrumbItem active>Vehicles</BreadcrumbItem>
          </Breadcrumb>
          
          <Row>
            <Col sm={12}>
              <Widget title="Vehicle Management">
                <div className="alert alert-danger">
                  Error loading vehicles: {error}
                  <br />
                  <Button color="primary" onClick={() => this.props.dispatch(fetchVehicles())}>
                    Retry
                  </Button>
                </div>
              </Widget>
            </Col>
          </Row>
        </div>
      );
    }

    return (
      <div>
        <Breadcrumb>
          <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
          <BreadcrumbItem active>Vehicles</BreadcrumbItem>
        </Breadcrumb>
        
        <Row>
          <Col sm={12}>
            <Widget
              title={
                <div>
                  <div className="pull-right mt-n-xs">
                    <Link 
                      to="/app/drivers/new" 
                      className="btn btn-success btn-sm"
                      title="Vehicles must be created with a driver. Click to create driver + vehicle."
                    >
                      <i className="fa fa-plus mr-1" />
                      Add New Vehicle
                    </Link>
                  </div>
                  <h5 className="mt-0 mb-3">
                    <i className="fa fa-truck mr-xs opacity-70" />{' '}
                    Vehicle Management
                  </h5>
                  <p className="fs-sm text-muted mb-0">
                    <i className="fa fa-info-circle mr-1 text-info" />
                    To add a new vehicle, create a driver first. Vehicles are automatically linked to drivers.
                  </p>
                </div>
              }
            >
              {/* ── Tab navigation ── */}
              <Nav tabs className="mb-3">
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'vehicles' })}
                    onClick={() => this.setState({ activeTab: 'vehicles' })}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="fa fa-truck mr-1" />
                    Vehicles
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'waiting' })}
                    onClick={() => { this.setState({ activeTab: 'waiting' }); this.fetchWaitingList(); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="fa fa-clock-o mr-1" />
                    Awaiting Terms
                    {this.state.waitingList.length > 0 && (
                      <Badge color="warning" className="ml-1">{this.state.waitingList.length}</Badge>
                    )}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'driver-pay' })}
                    onClick={() => this.setState({ activeTab: 'driver-pay' })}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="fa fa-money mr-1" />
                    Driver Pay
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>

                {/* ── Vehicles tab ── */}
                <TabPane tabId="vehicles">
                  <Row className="mb-3">
                    <Col sm={6}>
                      <InputGroup>
                        <Input 
                          placeholder="Search vehicles..." 
                          value={this.state.searchTerm}
                          onChange={this.handleSearch}
                        />
                        <InputGroupAddon addonType="append">
                          <Button color="default">
                            <i className="fa fa-search" />
                          </Button>
                        </InputGroupAddon>
                      </InputGroup>
                    </Col>
                    <Col sm={3}>
                      <ButtonDropdown 
                        isOpen={this.state.statusDropdownOpen} 
                        toggle={() => this.setState(prev => ({ statusDropdownOpen: !prev.statusDropdownOpen }))}
                      >
                        <DropdownToggle caret color="default">
                          Filter: {this.state.statusFilter === 'all' ? 'All Status' : this.state.statusFilter}
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem onClick={() => this.handleStatusFilter('all')}>All Status</DropdownItem>
                          <DropdownItem onClick={() => this.handleStatusFilter('active')}>Active</DropdownItem>
                          <DropdownItem onClick={() => this.handleStatusFilter('inactive')}>Inactive</DropdownItem>
                        </DropdownMenu>
                      </ButtonDropdown>
                    </Col>
                  </Row>

                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Registration No</th>
                        <th>Make &amp; Model</th>
                        <th>Class</th>
                        <th>Status</th>
                        <th>Active</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isFetching ? (
                        <tr>
                          <td colSpan="7" className="text-center">Loading vehicles...</td>
                        </tr>
                      ) : filteredVehicles.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">No vehicles found</td>
                        </tr>
                      ) : (
                        filteredVehicles.map(vehicle => (
                          <tr key={vehicle.vehicle_id}>
                            <td>
                              {vehicle.vehicle_id}
                              {vehicle.is_new && (
                                <Badge color="warning" className="ml-1">NEW</Badge>
                              )}
                            </td>
                            <td><strong>{vehicle.registration_no}</strong></td>
                            <td>{vehicle.make} {vehicle.model}</td>
                            <td>
                              <Badge color="info">{vehicle.class}</Badge>
                            </td>
                            <td>
                              <Badge color={this.getStatusBadge(vehicle.status)}>
                                {vehicle.status}
                              </Badge>
                            </td>
                            <td>
                              <Badge color={vehicle.is_active ? 'success' : 'secondary'}>
                                {vehicle.is_active ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <ButtonDropdown 
                                isOpen={this.state.dropdownOpen[vehicle.vehicle_id]} 
                                toggle={() => this.toggleDropdown(vehicle.vehicle_id)}
                                size="sm"
                              >
                                <DropdownToggle caret color="default" size="sm">
                                  Actions
                                </DropdownToggle>
                                <DropdownMenu>
                                  <DropdownItem tag={Link} to={`/app/vehicles/${vehicle.vehicle_id}/edit`}>
                                    <i className="fa fa-edit mr-1" /> Edit
                                  </DropdownItem>
                                  <DropdownItem divider />
                                  <DropdownItem onClick={() => this.handleUpdateVehicleState(vehicle.vehicle_id, 'active')}>
                                    <i className="fa fa-check-circle mr-1" /> Mark Active
                                  </DropdownItem>
                                  <DropdownItem onClick={() => this.handleUpdateVehicleState(vehicle.vehicle_id, 'inactive')}>
                                    <i className="fa fa-check mr-1" /> Mark inactive
                                  </DropdownItem>
                                  <DropdownItem divider />
                                  <DropdownItem 
                                    onClick={() => this.handleDeleteVehicle(vehicle.vehicle_id)}
                                    className="text-danger"
                                  >
                                    <i className="fa fa-trash mr-1" /> Delete
                                  </DropdownItem>
                                </DropdownMenu>
                              </ButtonDropdown>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </TabPane>

                {/* ── Awaiting Terms tab ── */}
                <TabPane tabId="waiting">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">
                      Drivers who have been sent the Terms &amp; Conditions and haven't agreed yet.
                      Once they tap "I Agree" on WhatsApp, they will automatically appear in the Vehicles tab.
                    </span>
                    <Button color="default" size="sm" onClick={this.fetchWaitingList}>
                      <i className="fa fa-refresh mr-1" /> Refresh
                    </Button>
                  </div>
                  {this.state.waitingListLoading ? (
                    <p className="text-center text-muted">Loading...</p>
                  ) : this.state.waitingList.length === 0 ? (
                    <p className="text-center text-muted">No drivers awaiting terms acceptance.</p>
                  ) : (
                    <Table responsive striped>
                      <thead>
                        <tr>
                          <th>Phone</th>
                          <th>Driver Name</th>
                          <th>Vehicle</th>
                          <th>Terms Sent At</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {this.state.waitingList.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.phone_number}</td>
                            <td>{row.driver_name}</td>
                            <td>{row.vehicle || '—'}</td>
                            <td>{row.sent_at ? new Date(row.sent_at).toLocaleString() : '—'}</td>
                            <td><Badge color="warning">Pending</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </TabPane>

                {/* ── Driver Pay tab ── */}
                <TabPane tabId="driver-pay">
                  <DriverPayTab />
                </TabPane>

              </TabContent>
            </Widget>
          </Col>
        </Row>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    vehicles: state.vehicles.vehicles,
    isFetching: state.vehicles.isFetching,
    error: state.vehicles.error,
  };
}

export default connect(mapStateToProps)(VehicleList);
